import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { buildStorageClassLabelMap } from '../../components/storageClassField';
import { VolumeResourceCard, buildDiskVolumeExtraItems } from '../../components/volumeResourceCard';
import styles from './index.module.less';
import type { DataDiskRecord } from './types';
import { diskToCardItem, normalizeDataDiskList } from './utils';

interface StoragePreviewSectionProps {
  disks?: DataDiskRecord[] | unknown;
  /** 表单值 + optionMeta，用于解析存储类展示名 */
  optionMetaForm?: Record<string, unknown>;
  disksPath?: string;
  pricingActive?: boolean;
  /** 只读预览下空列表是否隐藏整块，默认 true */
  hideEmptyList?: boolean;
}

/** 确认页/详情：数据盘完整卡片（无添加/编辑/删除） */
export function StoragePreviewSection({
  disks,
  optionMetaForm = {},
  disksPath = 'dataDisks',
  pricingActive = false,
  hideEmptyList = true,
}: StoragePreviewSectionProps) {
  const { t } = useTranslation();
  const diskList = useMemo(() => normalizeDataDiskList(disks), [disks]);
  const unconfiguredLabel = t('data-center:maas.storage.not.configured');
  const storageClassLabelMap = useMemo(
    () => buildStorageClassLabelMap(diskList, optionMetaForm, disksPath),
    [diskList, disksPath, optionMetaForm],
  );

  if (!diskList.length && hideEmptyList) {
    return null;
  }

  return (
    <div className={styles.list}>
      {diskList.map((disk, index) => {
        const cardItem = diskToCardItem(disk, storageClassLabelMap);
        return (
          <VolumeResourceCard
            key={`${cardItem.diskName}-${index}`}
            value={cardItem.diskName}
            label={cardItem.diskNickname || cardItem.diskName}
            extraItems={buildDiskVolumeExtraItems(cardItem, t, {
              emptyStorageClassFallback: !pricingActive ? unconfiguredLabel : undefined,
            })}
            canEdit={false}
            canDelete={false}
          />
        );
      })}
    </div>
  );
}
